import { useEffect, useMemo, useState } from "react";
import { MagnifyingGlassIcon, CheckCircleIcon } from "@phosphor-icons/react";
import api from "@/lib/api";
import { fmtL, fmtDate, apiErr } from "@/lib/format";
import { normalizar } from "@/components/LiveSearch";

/** Cliente de la nota: se busca mientras se escribe (nombre, código o RTN) y se elige de la lista. */
export function ClientePicker({ clients, value, onChange, disabled = false }) {
  const [q, setQ] = useState("");
  const elegido = clients.find((c) => c.id === value);
  const resultados = useMemo(() => {
    const palabras = normalizar(q).split(/\s+/).filter(Boolean);
    if (!palabras.length) return [];
    return clients.filter((c) => {
      const t = normalizar([c.codigo_cliente, c.codigo, c.nombre, c.rtn, c.identidad].filter(Boolean).join(" "));
      return palabras.every((w) => t.includes(w));
    }).slice(0, 8);
  }, [clients, q]);
  if (elegido) {
    return (
      <div className="flex items-center justify-between gap-2 bg-agro-green/5 border border-agro-green/40 rounded-md px-3 py-2 min-h-[44px]" data-testid="cn-cliente-elegido">
        <div className="min-w-0 text-sm">
          <div className="font-medium truncate">{elegido.nombre}</div>
          <div className="text-[11px] text-text-muted">{[elegido.codigo_cliente || elegido.codigo, elegido.rtn && `RTN ${elegido.rtn}`].filter(Boolean).join(" · ")}</div>
        </div>
        {!disabled && <button type="button" onClick={() => { onChange(""); setQ(""); }} data-testid="cn-cliente-cambiar"
          className="text-xs text-agro-green min-h-[36px] px-2 flex-shrink-0">Cambiar</button>}
      </div>
    );
  }
  return (
    <div>
      <label className="relative block">
        <span className="sr-only">Buscar cliente</span>
        <MagnifyingGlassIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" aria-hidden="true" />
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)} autoComplete="off" data-testid="cn-client-search"
          placeholder="Escribe el nombre, código o RTN del cliente…" disabled={disabled}
          className="w-full min-h-[44px] bg-app border border-border rounded-md pl-9 pr-3 py-2 text-sm" />
      </label>
      {q.trim() && (
        <ul className="mt-1 border border-border rounded-md divide-y divide-border/50 max-h-64 overflow-y-auto" data-testid="cn-cliente-resultados">
          {resultados.length === 0 ? <li className="p-3 text-xs text-text-muted">Sin coincidencias</li> : resultados.map((c) => (
            <li key={c.id}>
              <button type="button" onClick={() => { onChange(c.id); setQ(""); }} data-testid={`cn-cliente-op-${c.id}`}
                className="w-full text-left px-3 py-2 min-h-[44px] hover:bg-white/[0.04]">
                <div className="text-sm">{c.nombre}</div>
                <div className="text-[11px] text-text-muted">{[c.codigo_cliente || c.codigo, c.rtn && `RTN ${c.rtn}`].filter(Boolean).join(" · ")}</div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Facturas (y notas de débito) del cliente con saldo acreditable, filtradas por cuenta o subcuenta. */
export function FacturasCliente({ clienteId, cuenta, seleccion, onPick }) {
  const [docs, setDocs] = useState(null);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  useEffect(() => {
    setDocs(null); setError("");
    if (!clienteId) return;
    api.get("/credit-notes/eligible-documents", { params: { cliente_id: clienteId } })
      .then(({ data }) => setDocs(data || [])).catch((e) => { setError(apiErr(e)); setDocs([]); });
  }, [clienteId]);
  const visibles = useMemo(() => {
    const palabras = normalizar(q).split(/\s+/).filter(Boolean);
    return (docs || []).filter((d) => (cuenta === "" ? true : cuenta === "principal" ? !d.proyecto_id : d.proyecto_id === cuenta))
      .filter((d) => !palabras.length || palabras.every((w) => normalizar(`${d.numero} ${d.proyecto_codigo || ""}`).includes(w)));
  }, [docs, cuenta, q]);
  if (!clienteId) return <div className="text-xs text-text-muted">Primero elige el cliente.</div>;
  if (!docs) return <div className="text-xs text-text-muted">Cargando facturas del cliente…</div>;
  return (
    <div data-testid="cn-facturas">
      {(docs.length > 6) && (
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filtrar por número…" data-testid="cn-facturas-buscar"
          className="w-full min-h-[40px] mb-2 bg-app border border-border rounded-md px-3 text-sm" />
      )}
      {error && <div className="text-xs text-red-300 mb-2">{error}</div>}
      {visibles.length === 0 ? (
        <div className="text-xs text-text-muted border border-border rounded-md p-3" data-testid="cn-facturas-vacio">
          {docs.length ? "No hay facturas en esta cuenta." : "El cliente no tiene facturas con saldo para acreditar."}</div>
      ) : (
        <ul className="border border-border rounded-md divide-y divide-border/50 max-h-72 overflow-y-auto">
          {visibles.map((d) => {
            const activo = seleccion === d.id;
            return (
              <li key={`${d.tipo}-${d.id}`}>
                <button type="button" onClick={() => onPick(d)} data-testid={`cn-factura-op-${d.numero}`} aria-pressed={activo}
                  className={`w-full text-left px-3 py-2 min-h-[48px] flex items-center gap-3 ${activo ? "bg-agro-green/10" : "hover:bg-white/[0.04]"}`}>
                  {activo ? <CheckCircleIcon size={18} weight="fill" className="text-agro-green flex-shrink-0" /> : <span className="w-[18px] flex-shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-xs text-agro-green truncate">
                      {d.tipo === "nota_debito" && <span className="font-sans text-[9px] uppercase mr-1 text-agro-yellow">ND</span>}{d.numero}</div>
                    <div className="text-[11px] text-text-muted">{fmtDate(d.fecha_emision)} · {d.proyecto_codigo || "Cuenta principal"}</div>
                  </div>
                  <div className="text-right text-xs flex-shrink-0">
                    <div>Total {fmtL(d.monto_original)}</div>
                    <div className="text-text-muted">Saldo {fmtL(d.saldo_pendiente)}</div>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** Filtro de cuenta: todas, cuenta principal o cada subcuenta del cliente. */
export function CuentaSelect({ proyectos, value, onChange, disabled = false }) {
  if (!proyectos.length) return null;
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid="cn-cuenta" aria-label="Cuenta o subcuenta" disabled={disabled}
      className="w-full min-h-[44px] bg-app border border-border rounded-md px-3 text-sm">
      <option value="">Todas las cuentas</option>
      <option value="principal">Cuenta principal</option>
      {proyectos.map((p) => <option key={p.id} value={p.id}>{p.codigo} · {p.nombre}</option>)}
    </select>
  );
}
